import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_SETTINGS, getCachedSettings, loadSettings } from '../utils/settingsCache';

const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'BDT': '৳',
    'INR': '₹'
};

const SettingsContext = createContext();

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(() => getCachedSettings());
    const [loading, setLoading] = useState(() => !getCachedSettings());
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchSettings = async () => {
            const cachedSettings = getCachedSettings();
            if (cachedSettings) {
                setSettings(cachedSettings);
                setLoading(false);
                return;
            }

            try {
                const loadedSettings = await loadSettings();
                if (!isMounted) return;
                setSettings(loadedSettings);
            } catch (err) {
                if (!isMounted) return;
                console.log('Failed to load settings', err);
                setError(err);
                setSettings(DEFAULT_SETTINGS);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchSettings();

        return () => {
            isMounted = false;
        };
    }, []);

    const getCurrencySymbol = () => {
        return (settings && settings.currency && CURRENCY_SYMBOLS[settings.currency]) || '৳';
    };

    const refreshSettings = async () => {
        const loadedSettings = await loadSettings({ force: true });
        setSettings(loadedSettings);
        return loadedSettings;
    };

    const formatPrice = (price) => {
        return `${getCurrencySymbol()}${parseFloat(price).toFixed(2)}`;
    };

    const value = {
        settings,
        loading,
        error,
        refreshSettings,
        brandName: (settings && settings.brandName) || "Guraba's Choice",
        primaryColor: (settings && settings.primaryColor) || '#007bff',
        secondaryColor: (settings && settings.secondaryColor) || '#6c757d',
        currency: (settings && settings.currency) || 'BDT',
        currencySymbol: getCurrencySymbol(),
        formatPrice,
        socialLinks: (settings && settings.socialLinks) || [],
        facebookPixelId: (settings && settings.facebookPixelId) || '',
        seoTitle: (settings && settings.seoTitle) || '',
        seoDescription: (settings && settings.seoDescription) || '',
        seoKeywords: (settings && settings.seoKeywords) || '',
        seoAuthor: (settings && settings.seoAuthor) || '',
        seoImage: (settings && settings.seoImage) || '',
        seoCanonicalUrl: (settings && settings.seoCanonicalUrl) || '',
        seoRobots: (settings && settings.seoRobots) || 'index, follow',
        productListPagination: (settings && settings.productListPagination) || 'numbered',
        productListPageSize: Number(settings && settings.productListPageSize) || 12,
        address: (settings && settings.address) || '',
        whatsapp: (settings && settings.whatsapp) || '',
        contactWidget: (settings && settings.contactWidget) || DEFAULT_SETTINGS.contactWidget,
        brandLogo: (settings && settings.brandLogo) || '',
        siteFavicon: (settings && settings.siteFavicon) || '',
        appleTouchIcon: (settings && settings.appleTouchIcon) || '',
        showBrandLogoInNavbar: !!(settings && settings.showBrandLogoInNavbar),
        showBrandNameInNavbar: !(settings && settings.showBrandNameInNavbar === false),
        paymentMethods: (settings && settings.paymentMethods) || [],
        deliveryChargeSettings: (settings && settings.deliveryChargeSettings) || DEFAULT_SETTINGS.deliveryChargeSettings,
        couponSettings: (settings && settings.couponSettings) || DEFAULT_SETTINGS.couponSettings,
        productCardActions: (settings && settings.productCardActions) || DEFAULT_SETTINGS.productCardActions,
        productDisplayColors: (settings && settings.productDisplayColors) || DEFAULT_SETTINGS.productDisplayColors
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
